import { Component, forwardRef, OnInit } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { AttributionDao } from '@app/dao/attribution/attribution.dao';
import { DocumentDao } from '@app/dao/document/document.dao';
import { GedService } from '@app/providers/ged/ged.service';
import { UserService } from '@app/providers/user/user.service';
import * as _ from 'lodash';
import { combineLatest, Subject } from 'rxjs';
import { UserDao } from '../../../dao/user/user.dao';
import { EaDocumentFormBuilder } from './ea-document-form.builder';
import { countPdfPagesInFile } from '@app/components/pr-ged-picker/ged-document-tags';

@Component({
  selector: 'ea-document-form',
  templateUrl: './ea-document-form.component.html',
  styleUrls: ['./ea-document-form.component.scss'],
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaDocumentFormComponent),
    },
  ],
  standalone: false,
})
export class EaDocumentFormComponent
  extends EaDocumentFormBuilder
  implements OnInit
{
  gedCategories: any[] = [];
  gedOwners: any[] = [];
  gedProductCategories: any[] = [];
  gedDocuments: any[] = [];

  gedPartners: any[] = [];
  gedProducts: any[] = [];

  gedProductTypes: any[] = [];
  gedDocumentTypes: any[] = [];

  productCategoriesWithProductType: any[] = [];

  files: File[] = [];

  file: any;

  error: string;
  boolProduct: boolean = true;

  user: UserAuthentified;

  doneSubject: Subject<void> = new Subject<void>();

  isCristalisOrganization: boolean = false;
  cristalisOrganizationId: number = 251251251;
  private readonly IDENTITY_DOCUMENTS = ['CNI', 'PASSEPORT', 'TITRE_DE_SEJOUR'];
  private readonly TWO_SIDED_DOCUMENTS = ['CNI', 'TITRE_DE_SEJOUR'];
  private readonly MAX_IDENTITY_FILE_SIZE = 4 * 1024 * 1024; // 4 Mo

  constructor(
    protected formConstructor: PrFormConstructor,
    private documentDao: DocumentDao,
    protected authenticationService: AuthentificationService,
    private userDao: UserDao,
    private gedService: GedService,
    private userService: UserService,
    private attributionDao: AttributionDao,
  ) {
    super(formConstructor, authenticationService);
  }

  ngOnInit() {
    this.attributionDao.getOrganization().subscribe((data: any) => {
      this.isCristalisOrganization = this.cristalisOrganizationId === data.id;
    });

    combineLatest([
      this.documentDao.getReferences(),
      this.userService.getUser(),
      this.userDao.getOwners(),
      this.documentDao.getGedProvidersReference(),
      this.documentDao.getGedProductsReference(),
    ]).subscribe(([references, user, owners, gedProviders, gedProducts]) => {
      this.gedCategories = references.gedCategories;
      this.gedPartners =
        gedProviders?.map(({ id, name }) => ({ id, value: name })) || [];
      this.gedProducts =
        gedProducts?.map(({ id, name, providerId }) => ({
          id,
          value: name,
          providerId,
        })) || [];
      this.gedProductTypes = references.gedProductTypes;
      this.gedProductCategories = references.gedProductCategories;
      this.gedDocumentTypes = references.gedDocumentTypes;
      this.gedDocuments = references.gedDocuments;
      this.user = user;
      let item = this.extract().file;
      if (item != null) {
        if (
          item.type === 'application/pdf' ||
          item.type === 'image/jpeg' ||
          item.type === 'image/png' ||
          item.size < 20971520
        ) {
          this.files.push(item);
        }
        this.file = item;
      }

      this.productCategoriesWithProductType = [
        ...new Set(this.gedProductTypes.map(item => item.productCategory)),
      ].filter(item => item != null && item !== undefined);
      this.gedOwners = owners
        .filter(item => !item.endDate)
        .map(item => {
          return {
            code: item.id,
            label: item.label,
          };
        });
      this.gedOwners.push({
        code: null,
        label: 'En commun',
      });
    });
  }

  onLoadFiles(files: any) {
    this.files = files;
    if (this.files.length > 0) {
      this.error = null;
    }
  }

  get title() {
    return 'Ajoutez un nouveau document';
  }

  async onSubmit() {
    this.error = null;

    if (this.files.length === 0) {
      this.error = 'Veuillez saisir au moins un fichier';
      return;
    }
    const documentType = this.form.get('document')?.value;
    const conformityError = await this.validateIdentityDocument(
      documentType,
      this.files,
    );
    if (conformityError) {
      this.error = conformityError;
      return;
    }

    this.prFormService.loading();
    const formData = new FormData();
    for (const file of this.files) {
      formData.append('documentsFiles', file, file?.name);
    }

    const formValues = this.extract();
    delete formValues.file;

    formData.append(
      'document',
      new Blob([JSON.stringify(formValues)], {
        type: 'application/json',
      }),
    );

    this.documentDao.saveDocument(formData, formValues.accountId).subscribe(
      success => {
        this.gedService.saveOne(success);
        this.prFormService.loaded();
        this.doneSubject.next();
        this.notificationService.success('La sauvegarde a réussi');
      },
      err => {
        console.error(err);
        this.prFormService.loaded();
        this.error =
          err?.error?.message ||
          err?.error?.errors?.documentsFiles ||
          'La sauvegarde a échoué';
        this.notificationService.error(this.error);
      },
    );
  }
  private async validateIdentityDocument(
    documentType: string,
    files: File[],
  ): Promise<string | null> {
    if (!documentType || !this.IDENTITY_DOCUMENTS.includes(documentType)) {
      return null;
    }

    if (this.TWO_SIDED_DOCUMENTS.includes(documentType) && files.length !== 2) {
      return 'Vous devez charger deux fichiers, un pour le recto et un pour le verso';
    }
    if (documentType === 'PASSEPORT' && files.length !== 1) {
      return 'Vous devez charger un seul fichier pour ce type de document';
    }

    for (const file of files) {
      if (file && file.size > this.MAX_IDENTITY_FILE_SIZE) {
        return 'La taille du fichier dépasse 4 Mo';
      }
    }

    for (const file of files) {
      const pageCount = await countPdfPagesInFile(file);
      if (pageCount > 1) {
        return 'Chaque fichier doit comporter une seule page';
      }
    }

    return null;
  }


  gedDocumentsRetreive(e, t) {
    return _(this.gedDocuments)
      .filter(doc => {
        if (doc.category === e) {
          return doc;
        }
        if (doc.productCategory.includes(t)) {
          return doc;
        }
      })
      .value();
  }

  gedDocumentsTypeRetreive(e) {
    return _(this.gedDocumentTypes)
      .filter(doc => {
        if (doc.type === e) {
          return doc;
        }
      })
      .value();
  }

  gedProductsRetreive(e) {
    return _(this.gedProducts)
      .filter(pro => {
        if (pro.providerId === e) {
          return pro;
        }
      })
      .value();
  }

  gedProductTypesRetreive(e) {
    return _(this.gedProductTypes)
      .filter(sub => {
        if (sub.productCategory === e) {
          return sub;
        }
      })
      .value();
  }

  gedProductCategoriesRetreive(e) {
    return _(this.gedProductCategories)
      .filter(typ => {
        if (typ.category === e) {
          return typ;
        }
      })
      .value();
  }

  initFormWithCategory() {
    this.form.get('document').setValue(null);
    this.form.get('productCategory').setValue(null);
    this.form.get('dateExpired').setValue(null);
    this.form.get('productType').setValue(null);
    this.form.get('providerId').setValue(null);
    this.form.get('productId').setValue(null);
    this.form.get('isSigned').setValue(false);
  }

  initFormWithProductCategory() {
    this.form.get('document').setValue(null);
    this.form.get('dateExpired').setValue(null);
    this.form.get('productType').setValue(null);
    this.form.get('providerId').setValue(null);
    this.form.get('productId').setValue(null);
    this.form.get('isSigned').setValue(false);
  }

  initFormWithPartner() {
    this.form.get('productId').setValue(null);
  }

  initFormWithDocument() {
    this.form.get('isSigned').setValue(false);
    this.form.get('documentType').setValue(null);
  }

  hiddenProductType(e) {
    if (this.productCategoriesWithProductType.includes(e)) {
      return false;
    } else {
      return true;
    }
  }
}
